/**
 * Rendering rules for a pipeline where null is a first-class value.
 *
 * The one rule that matters: absent data renders as an em dash, never as 0. Upstream goes
 * to real trouble to turn a -40 sentinel into null rather than a number; printing "0" here
 * would hand that lie straight back to the operator.
 */

export const EM_DASH = '—'

export function num(v: number | null | undefined, digits = 1): string {
  return v == null || !Number.isFinite(v) ? EM_DASH : v.toFixed(digits)
}

export function int(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? EM_DASH : Math.round(v).toLocaleString('en-IN')
}

export function pct(v: number | null | undefined, digits = 0): string {
  return v == null || !Number.isFinite(v) ? EM_DASH : `${v.toFixed(digits)}%`
}

export function unit(v: number | null | undefined, suffix: string, digits = 1): string {
  return v == null || !Number.isFinite(v) ? EM_DASH : `${v.toFixed(digits)} ${suffix}`
}

/** Compact age: 42s, 7m, 3h, 2d. Input is epoch millis. */
export function ago(ts: number | null | undefined, now = Date.now()): string {
  if (!ts) return EM_DASH
  const s = Math.max(0, Math.round((now - ts) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  return `${Math.floor(s / 86400)}d`
}

export function clock(ts: number | null | undefined): string {
  if (!ts) return EM_DASH
  return new Date(ts).toTimeString().slice(0, 8)
}

/** Date + time together, e.g. "22 Sep, 09:14" -- for lists that can mix rows from
 *  different days (clock() alone reads as "today" no matter which day it actually was). */
export function dateTime(ts: number | null | undefined): string {
  if (!ts) return EM_DASH
  const d = new Date(ts)
  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${date}, ${time}`
}

export function duration(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return EM_DASH
  const s = Math.max(0, Math.round(sec))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${s % 60}s`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

// --------------------------------------------------------------------- vehicle state
export type VehicleState = 'stale' | 'charging' | 'moving' | 'idle'

/** Silent longer than this and the marker desaturates. It never disappears: a bus that
 *  stopped reporting is exactly the one an operator needs to still see on the map. */
/**
 * How long a truck may be silent before the console calls it "not reporting".
 *
 * THIS HAS TO BE LONGER THAN THE FEED'S OWN DELIVERY LAG, and it was not. Measured over 900
 * consecutive upstream messages: 94% arrive 10-30 MINUTES after the timestamp the device put
 * on them. That delay is upstream of anything here -- the messages hit the broker the instant
 * they are published (received_at matches the Kafka write to the millisecond), so the gap is
 * between the device recording a reading and the platform publishing it.
 *
 * At 10 minutes, EVERY truck on the corridor was stale by definition: all 54 in the live map
 * sat in the 10-20 minute band. The console read "55 not reporting" while the pipeline was
 * keeping pace exactly, 1.0x real time, with 53 messages of consumer lag. Nothing was wrong
 * except this number.
 *
 * 45 minutes covers the measured band with room for the tail, and is still short enough that
 * a genuinely dead device shows up within a shift. It belongs beside the measurement rather
 * than being tuned by feel: if the upstream delay ever improves, this should come down with
 * it, and the honest way to know is to measure again.
 */
export const STALE_AFTER_MS = 45 * 60 * 1000

export function lastSeen(v: { last_obd_ts: number; last_geo_ts: number }): number {
  return Math.max(v.last_obd_ts || 0, v.last_geo_ts || 0)
}

export function vehicleState(
  v: {
    last_obd_ts: number; last_geo_ts: number; moving: boolean
    charger_state: number | null; speed_kph: number | null; power_kw?: number | null
  },
  now = Date.now(),
): VehicleState {
  if (now - lastSeen(v) > STALE_AFTER_MS) return 'stale'
  const moving = v.moving || (v.speed_kph != null && v.speed_kph >= 1)
  // charger_state is a known-unreliable raw CAN signal on this fleet -- it reads 0 even
  // during active charging (see ROADMAP.md, DATA-NOTES.md). power_kw < 0 is the same
  // fallback estimateRange() (range.ts) also uses -- gated on !moving because the same
  // negative sign is regen braking while under way. The server has its own independent
  // fallback for the identical reason, but off pack-current direction and SOC trend, not
  // power_kw (serving/app.py's _bms_is_charging) -- no power_kw field crosses the wire.
  if ((v.charger_state != null && v.charger_state > 0)
    || (!moving && v.power_kw != null && v.power_kw < 0)) {
    return 'charging'
  }
  if (moving) return 'moving'
  return 'idle'
}

export const STATE_COLOR: Record<VehicleState, string> = {
  moving: '#00C896',
  idle: '#F5A623',
  charging: '#3B82F6',
  stale: '#4F566B',
}

/** Congestion tiers, matching quixapp/rules/congestion.py's thresholds (the Java
 *  CongestionEvalProcessor this used to reference was retired -- streams/ no longer
 *  exists, see commit 409798d). */
export function congestionTier(score: number | null | undefined): 't0' | 't1' | 't2' | 't3' {
  const s = score ?? 0
  if (s >= 0.8) return 't3'
  if (s >= 0.55) return 't2'
  if (s >= 0.35) return 't1'
  return 't0'
}

/** quixapp/rules/congestion.py's own ON_SITE_TYPES -- a cell whose centroid sits in one
 *  of these fences is scored (so the heatmap can still show real depot/plant/gate
 *  slowness as slow, which it genuinely is) but the backend deliberately never alerts on
 *  it: trucks queueing, loading, or parked at a plant read as near-zero speed and a high
 *  stopped_ratio the same way a genuine traffic jam would, and nothing about that score
 *  distinguishes "dwelling at the plant" from "stuck on the open road." Congestion UI
 *  (the corridor-pressure bar, the congestion heatmap layer) must check this before
 *  treating a high score as road congestion worth alarming an operator over -- GHAT is
 *  deliberately NOT included here (see congestion.py's own comment: a blocked ghat is
 *  real congestion, suppressing the type would delete that signal). */
const ON_SITE_FENCE_TYPES = new Set([
  'plant', 'depot', 'yard', 'hub', 'gate', 'queue', 'rest', 'activity',
])

export function isOnSiteCell(fenceTypes: string[] | null | undefined): boolean {
  return (fenceTypes ?? []).some((t) => ON_SITE_FENCE_TYPES.has(t))
}

/** Congestion tiers. Distinct from the status palette on purpose: a jammed road is not
 *  a vehicle fault, and one red for both teaches people they are the same problem. */
export const TIER_COLOR: Record<'t0' | 't1' | 't2' | 't3', string> = {
  t0: '#2C7A62',
  t1: '#F5A623',
  t2: '#F0762B',
  t3: '#F04B4B',
}

export const SEVERITY_COLOR: Record<string, string> = {
  INFO: '#8890A8',
  WARN: '#F5A623',
  CRITICAL: '#F04B4B',
}

export const FENCE_COLOR: Record<string, string> = {
  plant: '#A78BFA',
  depot: '#3B6EF7',
  yard: '#00C896',
  gate: '#F5A623',
  queue: '#F0762B',
  activity: '#7C89B8',
  toll: '#B57EDC',
  congestion: '#F04B4B',
  stop: '#8890A8',
  rest: '#4FB0A5',
  ghat: '#8890A8',
  bridge: '#6E7D8A',
}

export function fenceColor(type: string | null | undefined): string {
  return FENCE_COLOR[String(type ?? '').toLowerCase()] ?? '#8890A8'
}
